import { useCallback, useEffect, useRef, useState } from 'react';
import * as msal from "@azure/msal-browser";
import { useSDK } from "@contentful/react-apps-toolkit";

const useMsalToken = () => {
    const [accessToken, setAccessToken] = useState(null);
    const sdk = useSDK();
    const pcaRef = useRef(null);

    const acquireToken = useCallback(async (pca) => {
        const scopes = [`api://${sdk.parameters.installation.entraClientId}/FanHub.API`];
        const account = pca.getActiveAccount();
        const silentRequest = { scopes, account };

        try {
            const response = await pca.acquireTokenSilent(silentRequest);
            setAccessToken(response.accessToken);
            return response.accessToken;
        } catch (silentError) {
            if (silentError instanceof msal.InteractionRequiredAuthError) {
                const response = await pca.acquireTokenPopup(silentRequest);
                setAccessToken(response.accessToken);
                return response.accessToken;
            }
            throw silentError;
        }
    }, [sdk.parameters.installation.entraClientId]);

    useEffect(() => {
        const init = async () => {
            const pca = new msal.PublicClientApplication({
                auth: {
                    clientId: sdk.parameters.installation.entraClientId,
                    authority: `https://login.microsoftonline.com/${sdk.parameters.installation.entraTenantId}`,
                },
                cache: {
                    cacheLocation: 'sessionStorage',
                    storeAuthStateInCookie: false
                }
            });

            await pca.initialize();
            pcaRef.current = pca;

            // for debug
            // await pca.logoutPopup();
            // return;

            try {
                const accounts = pca.getAllAccounts();

                if (accounts.length === 0) {
                    // No accounts, need to login
                    const loginResponse = await pca.loginPopup({
                        scopes: [`api://${sdk.parameters.installation.entraClientId}/FanHub.API`],
                    });
                    pca.setActiveAccount(loginResponse.account);
                } else {
                    pca.setActiveAccount(accounts[0]);
                }

                await acquireToken(pca);
            } catch (error) {
                console.warn("Error during MSAL initialization or token acquisition", error);
                console.log("Retrying");
                if (error.errorCode === 'interaction_in_progress') {
                    console.warn('Interaction in progress, retrying after delay...');
                    setTimeout(init, 2000); // Retry after 2 second
                } else {
                    console.error('Popup token acquisition failed:', error);
                }
            }
        };

        init();
    }, [sdk.parameters.installation.entraClientId, sdk.parameters.installation.entraTenantId, acquireToken]);

    const reauthenticate = useCallback(async () => {
        const pca = pcaRef.current;
        if (!pca) return null;

        setAccessToken(null);

        try {
            // Force interactive login to get a fresh session
            const scopes = [`api://${sdk.parameters.installation.entraClientId}/FanHub.API`];
            const loginResponse = await pca.loginPopup({ scopes });
            pca.setActiveAccount(loginResponse.account);
            return await acquireToken(pca);
        } catch (error) {
            console.error('Re-authentication failed:', error);
            return null;
        }
    }, [sdk.parameters.installation.entraClientId, acquireToken]);

    return { accessToken, reauthenticate };
};

export default useMsalToken;